"use client"

import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Bookmark, Trash2 } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import type { PullSelectionPreset } from "@/lib/pull-selection-storage"

export interface SavedSelectionsMenuProps {
  presets: PullSelectionPreset[]
  /** False while nothing is selected, so there is nothing worth naming */
  canSave: boolean
  onSave: (name: string) => void
  onApply: (preset: PullSelectionPreset) => void
  onDelete: (name: string) => void
}

/**
 * The Fields step's presets: name the current selection, apply a saved one, or delete
 * it. Presets live in this browser only, which the popover says.
 */
export function SavedSelectionsMenu({ presets, canSave, onSave, onApply, onDelete }: SavedSelectionsMenuProps) {
  const { t, n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")

  const save = () => {
    if (name.trim() === "") return
    onSave(name)
    setName("")
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-xs">
          <Bookmark className="mr-1 h-3.5 w-3.5" /> {t("pull.savedSelections.title")}
          {presets.length > 0 && <span className="ml-1 text-muted-foreground">({n(presets.length)})</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3 p-3" align="end">
        <p className="text-xs text-muted-foreground">{t("pull.savedSelections.help")}</p>
        {presets.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t("pull.savedSelections.empty")}</p>
        ) : (
          <ul className="max-h-48 space-y-1 overflow-y-auto">
            {presets.map((preset) => (
              <li key={preset.name} className="flex items-center gap-2 text-xs">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-baseline gap-2 rounded-md px-2 py-1 text-left hover:bg-muted"
                  onClick={() => {
                    onApply(preset)
                    setOpen(false)
                  }}
                >
                  <span className="truncate font-medium">{preset.name}</span>
                  <span className="shrink-0 text-muted-foreground">{t((preset.fields.length === 1 ? "pull.savedSelections.fieldsCountOne" : "pull.savedSelections.fieldsCountOther"), { count: n(preset.fields.length) })}</span>
                </button>
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => onDelete(preset.name)} title={t("pull.savedSelections.delete", { name: preset.name })}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <Input
            className="h-7 text-xs"
            placeholder={t("pull.savedSelections.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            disabled={!canSave}
          />
          <Button size="sm" className="h-7 text-xs" onClick={save} disabled={!canSave || name.trim() === ""}>
            {t("common.actions.save")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
